import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Plus, Edit, Trash2, X, Save, Calendar, Upload, Image as ImageIcon } from 'lucide-react'
import { apiFetch, getEventImageUrl } from '../../config/api'
import PageHeader from '../../components/Dashboard/PageHeader'

interface Event {
  id: number
  slug: string
  title: string
  date: string
  time: string | null
  description: string
  full_description: string
  price: string
  image_url: string
  preview_image_url: string | null
  flyer_image_url: string | null
  location: string | null
  category: string | null
  status: 'Geplant' | 'Aktiv' | 'Abgeschlossen' | 'Abgesagt'
  featured: boolean
  created_at: string
  updated_at: string
}

export default function EventsManagement() {
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingEvent, setEditingEvent] = useState<Event | null>(null)
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    description: '',
    full_description: '',
    price: '',
    preview_image_url: '',
    flyer_image_url: '',
    location: '',
    category: '',
    status: 'Aktiv' as 'Geplant' | 'Aktiv' | 'Abgeschlossen' | 'Abgesagt',
    featured: false,
  })
  const [uploadingPreview, setUploadingPreview] = useState(false)
  const [uploadingFlyer, setUploadingFlyer] = useState(false)
  const [previewImagePreview, setPreviewImagePreview] = useState<string | null>(null)
  const [flyerImagePreview, setFlyerImagePreview] = useState<string | null>(null)
  const previewFileInputRef = useRef<HTMLInputElement>(null)
  const flyerFileInputRef = useRef<HTMLInputElement>(null)
  const formRef = useRef<HTMLDivElement>(null)

  const getAuthToken = () => {
    return localStorage.getItem('dashboard_token') || ''
  }

  const fetchEvents = async () => {
    try {
      setLoading(true)
      // Lade alle Events (nicht nur aktive) für Dashboard
      const response = await apiFetch('/events', {
        headers: {
          'Authorization': getAuthToken(),
        },
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setEvents(data.data || [])
        setError('')
      } else {
        setError(data.error || 'Fehler beim Laden der Events')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Fetch events error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents()
  }, [])

  const handleAddEvent = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.title || !formData.date) {
      setError('Titel und Datum sind erforderlich.')
      return
    }

    try {
      console.log('📤 Sende Event-Daten:', {
        title: formData.title,
        preview_image_url: formData.preview_image_url,
        flyer_image_url: formData.flyer_image_url,
        formData: formData
      })

      const response = await apiFetch('/events', {
        method: 'POST',
        headers: {
          'Authorization': getAuthToken(),
        },
        body: JSON.stringify({
          title: formData.title,
          date: formData.date,
          time: formData.time || null,
          description: formData.description,
          full_description: formData.full_description,
          price: formData.price,
          preview_image_url: formData.preview_image_url || null,
          flyer_image_url: formData.flyer_image_url || null,
          location: formData.location || null,
          category: formData.category || null,
          status: formData.status,
          featured: formData.featured,
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        console.log('✅ Event erfolgreich erstellt:', data.data)
        setShowAddForm(false)
        resetForm()
        fetchEvents()
      } else {
        console.error('❌ Event-Erstellung fehlgeschlagen:', data)
        setError(data.error || 'Fehler beim Erstellen des Events')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Add event error:', err)
    }
  }

  const handleUpdateEvent = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.title || !formData.date) {
      setError('Titel und Datum sind erforderlich.')
      return
    }

    if (!editingEvent) return

    try {
      const response = await apiFetch(`/events/${editingEvent.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': getAuthToken(),
        },
        body: JSON.stringify({
          title: formData.title,
          date: formData.date,
          time: formData.time || null,
          description: formData.description,
          full_description: formData.full_description,
          price: formData.price,
          preview_image_url: formData.preview_image_url,
          flyer_image_url: formData.flyer_image_url,
          location: formData.location || null,
          category: formData.category || null,
          status: formData.status,
          featured: formData.featured,
        }),
      })

      const data = await response.json()

      console.log('Update Event Response:', data)

      if (response.ok && data.success) {
        console.log('Event erfolgreich aktualisiert:', data.data)
        setEditingEvent(null)
        resetForm()
        fetchEvents()
      } else {
        console.error('Update Event Fehler:', data)
        setError(data.error || 'Fehler beim Aktualisieren des Events')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Update event error:', err)
    }
  }

  const handleDeleteEvent = async (eventId: number) => {
    if (!confirm('Möchten Sie dieses Event wirklich löschen?')) {
      return
    }

    try {
      const response = await apiFetch(`/events/${eventId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': getAuthToken(),
        },
      })

      const data = await response.json()

      if (response.ok && data.success) {
        fetchEvents()
      } else {
        setError(data.error || 'Fehler beim Löschen des Events')
      }
    } catch (err) {
      setError('Verbindungsfehler')
      console.error('Delete event error:', err)
    }
  }

  const startEdit = (event: Event) => {
    setEditingEvent(event)
    setFormData({
      title: event.title,
      date: event.date,
      time: event.time || '',
      description: event.description || '',
      full_description: event.full_description || '',
      price: event.price || '',
      preview_image_url: event.preview_image_url || event.image_url || '',
      flyer_image_url: event.flyer_image_url || '',
      location: event.location || '',
      category: event.category || '',
      status: event.status,
      featured: event.featured || false,
    })
    // Keine Pfade in Preview-State – Vorschaubilder kommen über getEventImageUrl(formData)
    setPreviewImagePreview(null)
    setFlyerImagePreview(null)
    setShowAddForm(false)
    
    // Scrolle zum Formular nach kurzer Verzögerung (damit React das Formular rendern kann)
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  const cancelEdit = () => {
    setEditingEvent(null)
    resetForm()
  }

  const resetForm = () => {
    setFormData({
      title: '',
      date: '',
      time: '',
      description: '',
      full_description: '',
      price: '',
      preview_image_url: '',
      flyer_image_url: '',
      location: '',
      category: '',
      status: 'Aktiv',
      featured: false,
    })
    setPreviewImagePreview(null)
    setFlyerImagePreview(null)
    if (previewFileInputRef.current) {
      previewFileInputRef.current.value = ''
    }
    if (flyerFileInputRef.current) {
      flyerFileInputRef.current.value = ''
    }
  }

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'preview' | 'flyer'
  ) => {
    console.log('🖼️ handleImageUpload called for type:', type)
    const file = e.target.files?.[0]
    if (!file) {
      console.log('❌ No file selected')
      return
    }
    console.log('📁 File selected:', file.name, file.type, file.size)

    const reader = new FileReader()
    reader.onloadend = () => {
      if (type === 'preview') {
        setPreviewImagePreview(reader.result as string)
      } else {
        setFlyerImagePreview(reader.result as string)
      }
    }
    reader.readAsDataURL(file)

    try {
      if (type === 'preview') {
        setUploadingPreview(true)
      } else {
        setUploadingFlyer(true)
      }
      setError('')

      const uploadFormData = new FormData()
      uploadFormData.append('image', file)
      uploadFormData.append('image_type', type)
      uploadFormData.append('alt_text', formData.title || `Event ${type === 'preview' ? 'Vorschaubild' : 'Flyer'}`)

      const apiBase = import.meta.env.VITE_API_BASE_URL || '/api'
      const response = await fetch(`${apiBase}/event_images.php`, {
        method: 'POST',
        headers: {
          'Authorization': getAuthToken(),
        },
        body: uploadFormData,
      })

      const data = await response.json()

      if (response.ok && data.success) {
        const imagePath = data.path || data.file_path
        
        console.log('✅ Bild hochgeladen:', imagePath, 'Type:', type)
        
        if (type === 'preview') {
          setFormData(prev => {
            console.log('📝 Setze preview_image_url:', imagePath)
            return { ...prev, preview_image_url: imagePath }
          })
        } else {
          setFormData(prev => {
            console.log('📝 Setze flyer_image_url:', imagePath)
            return { ...prev, flyer_image_url: imagePath }
          })
        }
      } else {
        setError(data.error || 'Fehler beim Hochladen des Bildes')
        if (type === 'preview') {
          setPreviewImagePreview(null)
        } else {
          setFlyerImagePreview(null)
        }
      }
    } catch (err) {
      setError('Verbindungsfehler beim Hochladen')
      if (type === 'preview') {
        setPreviewImagePreview(null)
      } else {
        setFlyerImagePreview(null)
      }
      console.error('Upload error:', err)
    } finally {
      if (type === 'preview') {
        setUploadingPreview(false)
      } else {
        setUploadingFlyer(false)
      }
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  return (
    <div className="w-full min-w-0 max-w-full">
      <PageHeader
        title="Events"
        description="Veranstaltungen anlegen, bearbeiten und veröffentlichen."
        actions={
          !showAddForm && !editingEvent ? (
            <button
              onClick={() => {
                setShowAddForm(true)
                setEditingEvent(null)
                resetForm()
                setTimeout(() => {
                  formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }, 100)
              }}
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-brand-800 text-white text-sm font-medium hover:bg-brand-700"
            >
              <Plus className="h-4 w-4" />
              Neues Event
            </button>
          ) : undefined
        }
      />

      {/* Error Message */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400"
        >
          {error}
        </motion.div>
      )}

      {/* Add/Edit Form - übersichtliches Content-Formular */}
      {(showAddForm || editingEvent) && (
        <motion.div
          ref={formRef}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 rounded-2xl border border-white/10 bg-zinc-900/95 shadow-xl overflow-hidden backdrop-blur-sm"
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-white/[0.03]">
            <h2 className="text-lg font-semibold text-white">
              {editingEvent ? 'Event bearbeiten' : 'Neues Event erstellen'}
            </h2>
            <button
              type="button"
              onClick={() => { setShowAddForm(false); cancelEdit() }}
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
              aria-label="Schließen"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6">
            <form onSubmit={editingEvent ? handleUpdateEvent : handleAddEvent} className="space-y-8">
              {/* Grunddaten */}
              <section className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
                <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">Grunddaten</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1.5">Titel *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="input-dashboard"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1.5">Datum *</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="input-dashboard"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1.5">Uhrzeit</label>
                    <input
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      className="input-dashboard"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1.5">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                      className="input-dashboard"
                    >
                      <option value="Geplant">Geplant</option>
                      <option value="Aktiv">Aktiv</option>
                      <option value="Abgeschlossen">Abgeschlossen</option>
                      <option value="Abgesagt">Abgesagt</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1.5">Preis</label>
                    <input
                      type="text"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="z.B. VVK 10€ / TK 15€"
                      className="input-dashboard"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1.5">Ort</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="z.B. Fest-Scheune"
                      className="input-dashboard"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1.5">Kategorie</label>
                    <input
                      type="text"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="z.B. Party, Karneval"
                      className="input-dashboard"
                    />
                  </div>
                  <div className="sm:col-span-2 flex items-center">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.featured}
                        onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                        className="w-4 h-4 rounded border-white/20 bg-white/5 text-brand-500 focus:ring-brand-500/50"
                      />
                      <span className="text-sm text-gray-300">Als Featured anzeigen (oben auf der Seite)</span>
                    </label>
                  </div>
                </div>
              </section>

              {/* Bilder – kompakte Thumbnails */}
              <section className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
                <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">Bilder</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1.5">Vorschaubild</label>
                      <p className="text-xs text-gray-500 mb-2">Für die Event-Übersicht (/partys)</p>
                      <motion.label
                        htmlFor="preview-upload"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-gray-300 hover:bg-white/10 hover:border-brand-500/30 cursor-pointer transition-colors"
                      >
                        <Upload className="h-4 w-4" />
                        {uploadingPreview ? 'Wird hochgeladen...' : 'Bild wählen'}
                      </motion.label>
                      <input
                        id="preview-upload"
                        ref={previewFileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'preview')}
                        disabled={uploadingPreview}
                        className="hidden"
                      />
                    </div>
                    {(previewImagePreview || formData.preview_image_url) && (
                      <div className="relative w-[200px] h-[140px] overflow-hidden rounded-lg border border-white/10 bg-black/30 flex-shrink-0">
                        <img
                          src={(previewImagePreview && (previewImagePreview.startsWith('blob:') || previewImagePreview.startsWith('data:'))) ? previewImagePreview : getEventImageUrl(formData.preview_image_url)}
                          alt="Vorschaubild"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1.5">Flyer</label>
                      <p className="text-xs text-gray-500 mb-2">Für die Event-Detailseite</p>
                      <motion.label
                        htmlFor="flyer-upload"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-gray-300 hover:bg-white/10 hover:border-brand-500/30 cursor-pointer transition-colors"
                      >
                        <Upload className="h-4 w-4" />
                        {uploadingFlyer ? 'Wird hochgeladen...' : 'Bild wählen'}
                      </motion.label>
                      <input
                        id="flyer-upload"
                        ref={flyerFileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'flyer')}
                        disabled={uploadingFlyer}
                        className="hidden"
                      />
                    </div>
                    {(flyerImagePreview || formData.flyer_image_url) && (
                      <div className="relative w-[200px] max-h-[200px] overflow-hidden rounded-lg border border-white/10 bg-black/30 flex-shrink-0">
                        <img
                          src={(flyerImagePreview && (flyerImagePreview.startsWith('blob:') || flyerImagePreview.startsWith('data:'))) ? flyerImagePreview : getEventImageUrl(formData.flyer_image_url)}
                          alt="Flyer"
                          className="w-full h-full object-contain max-h-[200px]"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* Beschreibung */}
              <section className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
                <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">Beschreibung</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1.5">Kurzbeschreibung</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="input-dashboard resize-y min-h-[80px]"
                      placeholder="Kurzer Text für die Übersicht"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1.5">Vollständige Beschreibung</label>
                    <textarea
                      value={formData.full_description}
                      onChange={(e) => setFormData({ ...formData, full_description: e.target.value })}
                      rows={5}
                      className="input-dashboard resize-y min-h-[120px]"
                      placeholder="Ausführlicher Text für die Detailseite"
                    />
                  </div>
                </div>
              </section>

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <motion.button
                  type="submit"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-sm font-medium transition-colors shadow-lg shadow-brand-600/20"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Save className="h-4 w-4" />
                  {editingEvent ? 'Speichern' : 'Event erstellen'}
                </motion.button>
                <button
                  type="button"
                  onClick={() => { setShowAddForm(false); cancelEdit() }}
                  className="px-5 py-2.5 rounded-xl border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 text-sm font-medium transition-colors"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      )}

      {/* Events Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative bg-gradient-to-br from-gray-900/80 to-gray-900/80 backdrop-blur-xl rounded-xl shadow-lg border border-white/10"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none" />
        
        <div className="relative z-10">
          {loading ? (
            <div className="p-12 text-center text-gray-300">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block"
              >
                <Calendar className="h-8 w-8" />
              </motion.div>
              <p className="mt-4">Lade Events...</p>
            </div>
          ) : events.length === 0 ? (
            <div className="p-12 text-center">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-300">Keine Events gefunden</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 min-w-0">
              {events.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -4 }}
                  className="group relative min-w-0 bg-gradient-to-br from-gray-900/90 to-gray-900/90 backdrop-blur-xl rounded-xl shadow-lg border border-white/10 hover:border-brand-500/30 transition-all duration-300"
                >
                  {/* Background Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-brand-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  {/* Event Image */}
                  {(event.preview_image_url || event.image_url) && (
                    <div className="relative h-48 overflow-hidden bg-black/50">
                      <img
                        src={getEventImageUrl(event.preview_image_url || event.image_url)}
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      
                      {/* Featured Badge */}
                      {event.featured && (
                        <div className="absolute top-3 left-3">
                          <span className="px-3 py-1 text-xs rounded-full bg-yellow-500/90 text-black font-bold backdrop-blur-sm shadow-lg flex items-center gap-1">
                            ⭐ Featured
                          </span>
                        </div>
                      )}
                      
                      {/* Status Badge */}
                      <div className="absolute top-3 right-3">
                        <span className={`px-3 py-1 text-xs rounded-full font-medium backdrop-blur-sm shadow-lg ${
                          event.status === 'Aktiv'
                            ? 'bg-emerald-500/90 text-white'
                            : event.status === 'Abgeschlossen'
                            ? 'bg-gray-500/90 text-white'
                            : event.status === 'Abgesagt'
                            ? 'bg-red-500/90 text-white'
                            : 'bg-yellow-500/90 text-black'
                        }`}>
                          {event.status}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Event Info */}
                  <div className="relative z-10 p-5">
                    {/* Title */}
                    <h3 className="text-xl font-semibold text-white mb-3 break-words group-hover:text-brand-400 transition-colors">
                      {event.title}
                    </h3>
                    
                    {/* Date & Time */}
                    <div className="flex items-center gap-2 text-gray-300 mb-3">
                      <Calendar className="h-4 w-4 text-brand-400" />
                      <span className="text-sm font-medium">
                        {formatDate(event.date)}
                        {event.time && ` • ${event.time} Uhr`}
                      </span>
                    </div>
                    
                    {/* Description */}
                    {event.description && (
                      <p className="text-sm text-gray-300 break-words mb-4 line-clamp-4">
                        {event.description}
                      </p>
                    )}
                    
                    {/* Price & Location */}
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400 mb-4 min-w-0">
                      {event.price && (
                        <span className="px-2 py-1 bg-brand-500/10 text-brand-400 rounded shrink-0">
                          {event.price}
                        </span>
                      )}
                      {event.location && (
                        <span className="break-words min-w-0 line-clamp-2">📍 {event.location}</span>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-3 border-t border-white/10">
                      <motion.button
                        onClick={() => startEdit(event)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-brand-500/10 hover:bg-brand-500/20 text-brand-400 rounded-lg transition-colors text-sm font-medium"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Edit className="h-4 w-4" />
                        Bearbeiten
                      </motion.button>
                      <motion.button
                        onClick={() => handleDeleteEvent(event.id)}
                        className="p-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title="Löschen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  )
}
